import * as React from 'react'
import { useEffect, useCallback, useMemo, memo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'

import { addDays, subDays } from 'date-fns'
import { useDate } from '@/modules/drivers/stores/dateStore'
import WeeklyNavigation from '../components/navigations/WeeklyNavigation'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { ActivityInfo } from '@/modules/drivers/types/activities'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import Timeline from '../components_new/Timeline'
import { TimelineSkeleton } from '../components_new/TimelineSkeleton'
import { TimelineProps } from '../components_new/timelineProps'
import {
  faEmptySetSolid,
  faClose,
  FaIcon,
  faMagnifyingGlassSolid,
} from '@/lib/fa'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import AddActivityModal from '@/modules/drivers/modals/activities/AddActivityModal'
import { useActivityContextMenu } from '@/modules/drivers/tabs/activities/context/ActivityContextMenuContext'
import {
  calculateSummary,
  getFormattedCalculation,
} from '@/app/components/timeline/timelineHelper'
import { ActivityType } from 'app/constants/activity'
import { useQueryDriver } from '@/lib/api/company/drivers'

const PL_WEEKDAY_ABBREV = ['Nd', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'Sb']

const ITEMS_PER_BLOCK = 8
const DAY_CHART_HEIGHT = 91

const formatDayHeader = (date: string): string => {
  const m = moment(date)
  return `${PL_WEEKDAY_ABBREV[m.day()].toUpperCase()}, ${m.format('DD-MM-YYYY')}`
}

const DayChart = memo(
  ({
    day,
    chartWidth,
    data,
    t,
    onDayClick,
    companyId,
    driverId,
    driverName,
    onRefresh,
    vehicleTachographMode,
    showViolations,
    rangeMode,
    rangeStart,
    rangeEnd,
    pin1Time: globalPin1,
    pin2Time: globalPin2,
    onRangeClick,
    showTrips = true,
    showTachographActivities = true,
  }: {
    day: { date: string; activities: any[]; count: number; isVisible: boolean }
    chartWidth: number
    data: ActivityInfo | null
    t: any
    onDayClick: (date: string) => void
    companyId: string
    driverId: number
    driverName: string
    onRefresh?: () => Promise<void>
    vehicleTachographMode?: boolean
    showViolations?: boolean
    showTachographActivities?: boolean
    rangeMode?: boolean
    rangeStart?: moment.Moment | null
    rangeEnd?: moment.Moment | null
    pin1Time?: moment.Moment | null
    pin2Time?: moment.Moment | null
    onRangeClick?: (time: moment.Moment) => void
    showTrips?: boolean
  }) => {
    const { setType } = useActivity()
    const { addActivity, editActivity } = useDriverActivityService()
    const dayStartTime = useMemo(
      () => moment(`${day.date} 00:00:00`),
      [day.date],
    )
    const [showAddActivityPopup, setShowAddActivityPopup] =
      React.useState(false)
    const [newActivity, setNewActivity] = React.useState(null)
    const [showEditActivityPopup, setShowEditActivityPopup] =
      React.useState(false)
    const [editedActivity, setEditedActivity] = React.useState(null)

    const [selectedActivity, setSelectedActivity] = React.useState(null)
    const { isOpen: isGlobalMenuOpen } = useActivityContextMenu()

    const hasAnyPin = globalPin1 != null || globalPin2 != null

    const dayChartWrapperRef = React.useRef<HTMLDivElement>(null)
    const [localHoverPos, setLocalHoverPos] = React.useState<number | null>(
      null,
    )
    const [localHoverTime, setLocalHoverTime] =
      React.useState<moment.Moment | null>(null)

    const CHART_OFFSET = 0
    const timelineStartX = CHART_OFFSET
    const usableTimelineWidth = chartWidth - CHART_OFFSET

    const dayStartMoment = useMemo(
      () => moment(day.date).startOf('day'),
      [day.date],
    )
    const dayEndMoment = useMemo(
      () => dayStartMoment.clone().add(1, 'day'),
      [dayStartMoment],
    )
    const totalSecondsInDay = useMemo(
      () => dayEndMoment.diff(dayStartMoment, 'seconds'),
      [dayStartMoment, dayEndMoment],
    )

    const timeToPosition = useCallback(
      (time: moment.Moment) => {
        const seconds = time.diff(dayStartMoment, 'seconds')
        const clamped = Math.max(0, Math.min(totalSecondsInDay, seconds))
        return (
          timelineStartX + (clamped / totalSecondsInDay) * usableTimelineWidth
        )
      },
      [dayStartMoment, totalSecondsInDay, timelineStartX, usableTimelineWidth],
    )

    const positionToTime = useCallback(
      (posX: number) => {
        const relX = posX - timelineStartX
        const fraction = Math.max(0, Math.min(1, relX / usableTimelineWidth))
        return dayStartMoment
          .clone()
          .add(fraction * totalSecondsInDay, 'seconds')
      },
      [dayStartMoment, totalSecondsInDay, timelineStartX, usableTimelineWidth],
    )

    const snapToEdge = useCallback(
      (time: moment.Moment) => {
        const allActivities = data?.activities || []
        const SNAP_THRESHOLD = 300
        let bestTime = time
        let bestDist = Infinity
        for (const activity of allActivities as any[]) {
          for (const edgeStr of [activity.startTime, activity.endTime]) {
            if (!edgeStr) continue
            const edge = moment(edgeStr)
            const dist = Math.abs(time.diff(edge, 'seconds'))
            if (dist < bestDist && dist <= SNAP_THRESHOLD) {
              bestDist = dist
              bestTime = edge.clone()
            }
          }
        }
        return bestTime
      },
      [data?.activities],
    )

    const handleRangeChartClick = useCallback(
      (e: React.MouseEvent) => {
        if (!dayChartWrapperRef.current || !onRangeClick) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        onRangeClick(snappedTime)
      },
      [positionToTime, snapToEdge, onRangeClick],
    )

    const handleRangeMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!dayChartWrapperRef.current) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        setLocalHoverPos(timeToPosition(snappedTime))
        setLocalHoverTime(snappedTime)
      },
      [positionToTime, snapToEdge, timeToPosition],
    )

    const handleRangeMouseLeave = useCallback(() => {
      setLocalHoverPos(null)
      setLocalHoverTime(null)
    }, [])

    const handleFirstPinClick = useCallback(
      (e: React.MouseEvent) => {
        if (hasAnyPin || !dayChartWrapperRef.current || !onRangeClick) return
        const rect = dayChartWrapperRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        if (x < CHART_OFFSET) return
        const rawTime = positionToTime(x)
        const snappedTime = snapToEdge(rawTime)
        onRangeClick(snappedTime)
      },
      [hasAnyPin, positionToTime, snapToEdge, onRangeClick],
    )

    const dayOverlay = useMemo(() => {
      if (!rangeStart || !rangeEnd) return null
      const hasOverlap =
        rangeStart.isBefore(dayEndMoment) && rangeEnd.isAfter(dayStartMoment)
      if (!hasOverlap) return { type: 'outside' as const }
      const overlapStart = moment.max(rangeStart, dayStartMoment)
      const overlapEnd = moment.min(rangeEnd, dayEndMoment)
      const startPos = timeToPosition(overlapStart)
      const endPos = timeToPosition(overlapEnd)
      return {
        type: 'partial' as const,
        leftOverlayWidth: startPos,
        selectionLeft: startPos,
        selectionWidth: endPos - startPos,
        rightOverlayLeft: endPos,
        rightOverlayWidth: chartWidth - endPos,
      }
    }, [
      rangeStart,
      rangeEnd,
      dayStartMoment,
      dayEndMoment,
      timeToPosition,
      chartWidth,
    ])

    const pin1PosOnDay = useMemo(() => {
      if (
        !globalPin1 ||
        !globalPin1.isSameOrAfter(dayStartMoment) ||
        !globalPin1.isBefore(dayEndMoment)
      )
        return null
      return timeToPosition(globalPin1)
    }, [globalPin1, dayStartMoment, dayEndMoment, timeToPosition])

    const pin2PosOnDay = useMemo(() => {
      if (
        !globalPin2 ||
        !globalPin2.isSameOrAfter(dayStartMoment) ||
        !globalPin2.isBefore(dayEndMoment)
      )
        return null
      return timeToPosition(globalPin2)
    }, [globalPin2, dayStartMoment, dayEndMoment, timeToPosition])

    const activities = data?.activities || []
    const formatedDate = moment(dayStartTime)

    const filteredActivities = useMemo(() => {
      return activities.filter((activity) => {
        const dayStart = formatedDate.clone()
        const dayEnd = formatedDate.clone().add(1, 'day')
        const activityEnd = activity.endTime
          ? moment(activity.endTime)
          : moment(formatedDate).endOf('day')

        return (
          moment(activity.startTime).isBefore(dayEnd) &&
          activityEnd.isAfter(dayStart)
        )
      })
    }, [activities, formatedDate])

    const filteredVehicleSourceActivities = useMemo(() => {
      if (!showTachographActivities) return []
      return (data?.vehicleSourceActivities || []).filter((activity) => {
        const dayStart = formatedDate.clone()
        const dayEnd = formatedDate.clone().add(1, 'day')
        const activityEnd = activity.endTime
          ? moment(activity.endTime)
          : moment(formatedDate).endOf('day')
        return (
          moment(activity.startTime).isBefore(dayEnd) &&
          activityEnd.isAfter(dayStart)
        )
      })
    }, [data?.vehicleSourceActivities, formatedDate, showTachographActivities])

    const filteredPlaces = useMemo(() => {
      const places = data?.places || []
      if (!vehicleTachographMode) {
        return places.filter((p) => !p.isAutomaticBorder)
      }
      return places
    }, [data?.places, vehicleTachographMode])

    const dayData = useMemo(() => {
      const startInDay = (time: any) => {
        const tt = moment(time)
        return tt.isSameOrAfter(dayStartMoment) && tt.isBefore(dayEndMoment)
      }
      const overlapsDay = (start: any, endRaw: any) => {
        const s = moment(start)
        const e = endRaw ? moment(endRaw) : s
        return s.isBefore(dayEndMoment) && e.isAfter(dayStartMoment)
      }
      const byOverlap =
        (endKey: string) =>
        (item: any) =>
          overlapsDay(item.startTime, item[endKey] ?? item.endTime)

      return {
        places: filteredPlaces.filter((p: any) => startInDay(p.startTime)),
        cardInserts: (data?.cardInserts || []).filter(byOverlap('endTime')),
        dayActivities: (data?.dayActivities || []).filter(byOverlap('endTime')),
        nightWorkActivities: (data?.nightWorkActivities || []).filter(
          byOverlap('endTime'),
        ),
        violations: (data?.violations || []).filter(byOverlap('endTime')),
        outOfScopes: (data?.outOfScopes || []).filter(byOverlap('endTime')),
        breaks: (data?.breaks || []).filter(byOverlap('formalRestEndDate')),
        partialBreaks: (data?.partialBreaks || []).filter(
          byOverlap('formalRestEndDate'),
        ),
        dailyRests: (data?.dailyRests || []).filter(
          byOverlap('formalRestEndDate'),
        ),
        dailyPartialRegularRests: (
          data?.dailyPartialRegularRests || []
        ).filter(byOverlap('formalRestEndDate')),
        weeklyRests: (data?.weeklyRests || []).filter(byOverlap('endTime')),
        weeklyReducedRests: (data?.weeklyReducedRests || []).filter(
          byOverlap('endTime'),
        ),
        weekRecompenses: (data?.weekRecompenses || []).filter(
          byOverlap('realRestEndTime'),
        ),
      }
    }, [data, filteredPlaces, dayStartMoment, dayEndMoment])

    const handleActivityAdd = useCallback((activity: any) => {
      const activityWithMoment = {
        ...activity,
        startTime: moment.isMoment(activity.startTime)
          ? activity.startTime
          : moment(activity.startTime),
        endTime: moment.isMoment(activity.endTime)
          ? activity.endTime
          : moment(activity.endTime),
      }

      setNewActivity(activityWithMoment)
      setShowAddActivityPopup(true)
    }, [])

    const handleDirectEdit = useCallback(
      async (activity: any) => {
        try {
          await editActivity(companyId, driverId, activity)

          if (onRefresh) {
            await onRefresh()
          }
        } catch (error) {
          console.error('Error editing activity flags:', error)
        }
      },
      [editActivity, companyId, driverId, onRefresh],
    )

    const handleActivityEdit = useCallback(
      (activity: any, action?: string) => {
        if (
          action === 'Crew' ||
          action === 'OutOfScope' ||
          action === 'FerryCrossing'
        ) {
          handleDirectEdit(activity)
          return
        }
        const activityWithMoment = {
          ...activity,
          startTime: moment.isMoment(activity.startTime)
            ? activity.startTime
            : moment(activity.startTime),
          endTime: moment.isMoment(activity.endTime)
            ? activity.endTime
            : moment(activity.endTime),
        }

        setEditedActivity(activityWithMoment)
        setShowEditActivityPopup(true)
      },
      [handleDirectEdit],
    )
    const handleCloseAddPopup = () => {
      setShowAddActivityPopup(false)
      setNewActivity(null)
    }

    const handleCloseEditPopup = () => {
      setShowEditActivityPopup(false)
      setEditedActivity(null)
    }
    React.useEffect(() => {
      setType('weekly')
    }, [])

    // Memoized so the hover indicator can repaint on every mouse move without
    // re-rendering the whole day timeline underneath it.
    const timeline = useMemo(
      () => (
        <Timeline
          key={`weekly-day-${day.date}`}
          {...({
            startTime: dayStartTime,
            days: 1,
            width: chartWidth,
            height: DAY_CHART_HEIGHT,
            extraCurrentDate: day.date,
            translate: t,
            activities: filteredActivities,
            vehicleSourceActivities: filteredVehicleSourceActivities,
            places: dayData.places,
            cardInserts: dayData.cardInserts,
            dayActivities: dayData.dayActivities,
            nightWorkActivities: dayData.nightWorkActivities,
            violations: dayData.violations,
            dailyRests: dayData.dailyRests,
            dailyPartialRegularRests: dayData.dailyPartialRegularRests,
            weeklyRests: dayData.weeklyRests,
            weeklyReducedRests: dayData.weeklyReducedRests,
            breaks: dayData.breaks,
            partialBreaks: dayData.partialBreaks,
            weekRecompenses: dayData.weekRecompenses,
            existingDelegations: [],
            outOfScopes: dayData.outOfScopes,
            changeAreas: [],
            tmsData: [],
            minimalPaymentCountries: [],
            periodSelections: [],
            delegationMarkers: [],
            allEvents: [],
            allTripEvents: [],
            allPreviousTripEvents: [],
            countries: [],
            canEdit: true,
            canGenerateAttestation: false,
            selectedActivity: selectedActivity,
            editedActivity: null,
            isAddingNew: false,
            flattenChart: true,
            simpleMode: false,
            disableTooltips: isGlobalMenuOpen || hasAnyPin,
            showViolations: showViolations,
            showTrips: false,
            showRests: true,
            showRestLabels: true,
            showRestParts: true,
            showDayActivities: true,
            showDelegations: false,
            editMode: false,
            onSelected: setSelectedActivity,
            onContextMenuChanged: () => {},
            onAdd: handleActivityAdd,
            onEdit: handleActivityEdit,
            onViolationSelected: () => {},
            onPeriodSelectionsChanged: () => {},
          } as unknown as TimelineProps)}
        />
      ),
      [
        day.date,
        dayStartTime,
        chartWidth,
        t,
        filteredActivities,
        filteredVehicleSourceActivities,
        dayData,
        selectedActivity,
        isGlobalMenuOpen,
        hasAnyPin,
        showViolations,
        handleActivityAdd,
        handleActivityEdit,
      ],
    )

    return (
      <div
        className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded tw-bg-white tw-shadow-sm"
        style={{
          opacity: dayOverlay?.type === 'outside' ? 0.3 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <div className="tw-flex tw-items-center tw-gap-2  tw-bg-white tw-px-3 tw-py-1.5">
          <div
            className="tw-grid tw-cursor-pointer hover:tw-opacity-70"
            onClick={() => onDayClick(day.date)}
          >
            <FaIcon icon={faMagnifyingGlassSolid} className="tw-h-3 tw-w-3" />
          </div>
          <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base">
            {formatDayHeader(day.date)}
          </span>
        </div>
        <div
          ref={dayChartWrapperRef}
          className="tw-relative"
          onMouseMove={rangeMode ? undefined : handleRangeMouseMove}
          onMouseLeave={rangeMode ? undefined : handleRangeMouseLeave}
        >
          {timeline}
        {rangeMode && (
          <>
            <div
              style={{
                position: 'absolute',
                left: CHART_OFFSET,
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 500,
                cursor: 'crosshair',
              }}
              onClick={handleRangeChartClick}
              onMouseMove={handleRangeMouseMove}
              onMouseLeave={handleRangeMouseLeave}
            />
            {dayOverlay?.type === 'partial' && (
              <>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: dayOverlay.leftOverlayWidth,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.75)',
                    zIndex: 400,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: dayOverlay.rightOverlayLeft,
                    top: 0,
                    width: dayOverlay.rightOverlayWidth,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.75)',
                    zIndex: 400,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: dayOverlay.selectionLeft,
                    top: 0,
                    width: dayOverlay.selectionWidth,
                    bottom: 0,
                    background: 'rgba(74, 96, 255, 0.08)',
                    border: '1px dashed rgba(74, 96, 255, 0.4)',
                    zIndex: 401,
                    pointerEvents: 'none',
                  }}
                />
              </>
            )}
            {pin1PosOnDay != null && (
              <div
                style={{
                  position: 'absolute',
                  left: pin1PosOnDay,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'var(--et-color-semantic-primary-foreground)',
                  zIndex: 450,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: -16,
                    left: -16,
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--et-color-semantic-primary-foreground)',
                    padding: '1px 4px',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {globalPin1?.format('HH:mm')}
                </div>
              </div>
            )}
            {pin2PosOnDay != null && (
              <div
                style={{
                  position: 'absolute',
                  left: pin2PosOnDay,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'var(--et-color-semantic-primary-foreground)',
                  zIndex: 450,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: -16,
                    left: -16,
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--et-color-semantic-primary-foreground)',
                    padding: '1px 4px',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {globalPin2?.format('HH:mm')}
                </div>
              </div>
            )}
          </>
        )}

        {localHoverPos != null && (
          <div
            style={{
              position: 'absolute',
              left: localHoverPos,
              top: 0,
              bottom: 0,
              width: 1,
              background: 'rgba(74, 96, 255, 0.5)',
              zIndex: 460,
              pointerEvents: 'none',
            }}
          >
            {localHoverTime && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  ...(localHoverPos > chartWidth - 40
                    ? { right: 4 }
                    : { left: 4 }),
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--et-color-semantic-primary-foreground)',
                  background: 'rgba(255,255,255,0.9)',
                  padding: '0 3px',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {localHoverTime.format('HH:mm')}
              </div>
            )}
          </div>
        )}
        </div>
        {showAddActivityPopup && (
          <AddActivityModal
            translate={t}
            type="add"
            activity={newActivity}
            onClose={handleCloseAddPopup}
            driverName={driverName}
            onRefresh={onRefresh}
            companyId={companyId}
            driverId={driverId}
          />
        )}
        {showEditActivityPopup && (
          <AddActivityModal
            translate={t}
            type="edit"
            activity={editedActivity}
            onClose={handleCloseEditPopup}
            driverName={driverName}
            onRefresh={onRefresh}
            companyId={companyId}
            driverId={driverId}
          />
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.day.date === nextProps.day.date &&
      prevProps.day.count === nextProps.day.count &&
      prevProps.chartWidth === nextProps.chartWidth &&
      prevProps.data === nextProps.data &&
      prevProps.driverName === nextProps.driverName &&
      prevProps.vehicleTachographMode === nextProps.vehicleTachographMode &&
      prevProps.showViolations === nextProps.showViolations &&
      prevProps.rangeMode === nextProps.rangeMode &&
      prevProps.rangeStart?.valueOf() === nextProps.rangeStart?.valueOf() &&
      prevProps.rangeEnd?.valueOf() === nextProps.rangeEnd?.valueOf() &&
      prevProps.pin1Time?.valueOf() === nextProps.pin1Time?.valueOf() &&
      prevProps.pin2Time?.valueOf() === nextProps.pin2Time?.valueOf() &&
      prevProps.showTrips === nextProps.showTrips &&
      prevProps.showTachographActivities === nextProps.showTachographActivities
    )
  },
)

const Weekly = ({
  showViolations,
  showTrips = true,
  showTachographActivities = true,
}: {
  showViolations?: boolean
  showTrips?: boolean
  showTachographActivities?: boolean
}) => {
  const {
    companyId,
    driverId,
    date: currentDate,
  } = useParams<{
    companyId: string
    driverId: string
    date: string
  }>()
  const navigate = useNavigate()
  const activityBasePath = `/et/company/${companyId}/driverModule/activities/driver/${driverId}`
  const { setSingle, formatApiDate } = useDate()
  const { data: driverInfo } = useQueryDriver({
    companyId,
    driverId,
    withPersonalData: false,
  })
  const driverName = driverInfo?.fullName || ''
  const { getDriverActivity } = useDriverActivityService()
  const { t } = useLocalize()
  const { refreshTrigger } = useActivity()
  const [data, setData] = React.useState<ActivityInfo | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [allDays, setAllDays] = React.useState<
    Array<{
      date: string
      activities: any[]
      count: number
      isVisible: boolean
    }>
  >([])

  const { vehicleTachographMode, setHasVehicleSourceActivities, setHasTachographEvents } =
    useActivity()

  React.useEffect(() => {
    setHasVehicleSourceActivities((data?.vehicleSourceActivities?.length ?? 0) > 0)
    // The weekly view has no tachograph-events table, so that toggle is daily-only.
    setHasTachographEvents(false)
  }, [data])

  const [rangeMode, setRangeMode] = React.useState(false)
  const [pin1Time, setPin1Time] = React.useState<moment.Moment | null>(null)
  const [pin2Time, setPin2Time] = React.useState<moment.Moment | null>(null)

  const hasAnyPin = pin1Time != null || pin2Time != null

  const handleRangeClick = useCallback(
    (time: moment.Moment) => {
      if (!pin1Time) {
        setPin1Time(time)
      } else if (!pin2Time) {
        setPin2Time(time)
      } else {
        setPin1Time(time)
        setPin2Time(null)
      }
    },
    [pin1Time, pin2Time],
  )

  const clearRange = useCallback(() => {
    setPin1Time(null)
    setPin2Time(null)
  }, [])

  const toggleRangeMode = useCallback(() => {
    setRangeMode((prev) => {
      if (prev) {
        setPin1Time(null)
        setPin2Time(null)
      }
      return !prev
    })
  }, [])

  const sortedRange = useMemo(() => {
    if (!pin1Time || !pin2Time) return null
    if (pin1Time.isBefore(pin2Time)) return { start: pin1Time, end: pin2Time }
    return { start: pin2Time, end: pin1Time }
  }, [pin1Time, pin2Time])

  const rangeSummary = useMemo(() => {
    if (!sortedRange || !data?.activities) return null

    const allActivities = (data.activities as any[]).map((a) => ({
      ...a,
      endTime: a.endTime || moment(a.startTime).endOf('day').toISOString(),
    }))

    const summary = calculateSummary(
      sortedRange.start,
      sortedRange.end,
      allActivities,
    )
    const formatted = getFormattedCalculation(summary)
    const totalMs = sortedRange.end.diff(sortedRange.start)
    const totalDuration = moment.duration(totalMs)
    const totalHours = Math.floor(totalDuration.asHours())
    const totalMinutes = totalDuration.minutes()

    return {
      ...formatted,
      totalSelected: `${totalHours}h ${totalMinutes.toString().padStart(2, '0')}min`,
      period: `${sortedRange.start.format('DD.MM HH:mm')} - ${sortedRange.end.format('DD.MM HH:mm')}`,
    }
  }, [sortedRange, data?.activities])

  const [scrollOffset, setScrollOffset] = React.useState(0)
  const isScrollingRef = React.useRef(false)
  const centerDateRef = React.useRef(currentDate)

  const getWeeklyActivity = async () => {
    try {
      setIsLoading(true)
      const dateObj = new Date(centerDateRef.current)
      const result = await getDriverActivity({
        companyId,
        driverId: Number(driverId),
        startTime: formatApiDate(subDays(dateObj, 25)),
        endTime: formatApiDate(addDays(dateObj, 25)),
        includeTrips: true,
        includeVehicleTachographData: false,
      })
      setData(result || null)

      const days: Array<{
        date: string
        activities: any[]
        count: number
        isVisible: boolean
      }> = []

      for (let i = -25; i <= 25; i++) {
        const dayDate = addDays(dateObj, i)
        const dayStr = moment(dayDate).format('YYYY-MM-DD')
        const dayActivities =
          result?.activities?.filter((activity) => {
            const activityDate = moment(activity.startTime).format('YYYY-MM-DD')
            return activityDate === dayStr
          }) || []

        days.push({
          date: dayStr,
          activities: dayActivities,
          count: dayActivities.length,
          isVisible: false,
        })
      }

      setAllDays(days)
     
      setScrollOffset(25)
      setIsLoading(false)
    } catch (error) {
      console.error('Weekly: Error fetching weekly activity:', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isScrollingRef.current) {
      centerDateRef.current = currentDate
      getWeeklyActivity()
    } else {
      isScrollingRef.current = false
    }
  }, [currentDate, driverId, companyId, vehicleTachographMode])

  React.useLayoutEffect(() => {
    setSingle(currentDate)
  }, [currentDate])

  React.useEffect(() => {
    if (refreshTrigger > 0) {
      getWeeklyActivity()
    }
  }, [refreshTrigger])

  const visibleDays = useMemo(() => {
    const startIdx = scrollOffset
    const endIdx = startIdx + ITEMS_PER_BLOCK
    return allDays.map((day, idx) => ({
      ...day,
      isVisible: idx >= startIdx && idx < endIdx,
    }))
  }, [allDays, scrollOffset])

  const maxScroll = Math.max(0, allDays.length - ITEMS_PER_BLOCK)

  const replaceWeekDate = (date: string) => {
    navigate(`${activityBasePath}/weeklyActivity/${date}`, { replace: true })
  }

  const goToPreviousBlock = useCallback(() => {
    if (isLoading) return

    if (scrollOffset <= 2 && allDays.length > 0) {
      const firstDay = allDays[0]
      const newCenterDate = moment(firstDay.date)
        .subtract(7, 'days')
        .format('YYYY-MM-DD')
      isScrollingRef.current = false
      replaceWeekDate(newCenterDate)
      return
    }

    setScrollOffset((prev) => {
      const newOffset = Math.max(0, prev - 1)
      if (allDays.length > 0) {
        const firstVisibleDay = allDays[newOffset]
        if (firstVisibleDay) {
          isScrollingRef.current = true
          replaceWeekDate(firstVisibleDay.date)
        }
      }
      return newOffset
    })
  }, [scrollOffset, allDays, activityBasePath, isLoading])

  const goToNextBlock = useCallback(() => {
    if (isLoading) return

    if (scrollOffset >= maxScroll - 2 && allDays.length > 0) {
      const lastDay = allDays[allDays.length - 1]
      const newCenterDate = moment(lastDay.date)
        .add(7, 'days')
        .format('YYYY-MM-DD')
      isScrollingRef.current = false
      replaceWeekDate(newCenterDate)
      return
    }

    setScrollOffset((prev) => {
      const newOffset = Math.min(maxScroll, prev + 1)
      if (allDays.length > 0) {
        const firstVisibleDay = allDays[newOffset]
        if (firstVisibleDay) {
          isScrollingRef.current = true
          replaceWeekDate(firstVisibleDay.date)
        }
      }
      return newOffset
    })
  }, [scrollOffset, maxScroll, allDays, activityBasePath, isLoading])

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = React.useState(0)
  const prevButtonRef = React.useRef<HTMLButtonElement>(null)
  const nextButtonRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        setChartWidth(wrapperRef.current.offsetWidth)
      }
    }
    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current)
    }
    window.addEventListener('resize', updateWidth)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  const daysToRender = useMemo(() => {
    const startIdx = scrollOffset
    const endIdx = startIdx + ITEMS_PER_BLOCK
    return visibleDays.slice(startIdx, endIdx)
  }, [visibleDays, scrollOffset])

  const handleDayClick = useCallback(
    (date: string) => {
      navigate(`${activityBasePath}/dailyActivity/${date}`)
    },
    [navigate, activityBasePath],
  )

  const skeletonDays = useMemo(
    () =>
      Array.from({ length: ITEMS_PER_BLOCK }, (_, i) =>
        moment(currentDate).add(i, 'days').format('YYYY-MM-DD'),
      ),
    [currentDate],
  )

  const currentDateRange = useMemo(() => {
    const dates =
      daysToRender.length > 0 ? daysToRender.map((d) => d.date) : skeletonDays
    const firstMoment = moment(dates[0])
    const lastMoment = moment(dates[dates.length - 1])

    const firstDay = firstMoment.format('D')
    const lastDay = lastMoment.format('D')
    const firstMonth = t(
      `monthName.${firstMoment.format('MMMM')?.toLowerCase()}`,
    )
    const lastMonth = t(`monthName.${lastMoment.format('MMMM')?.toLowerCase()}`)
    const firstYear = firstMoment.format('YYYY')
    const lastYear = lastMoment.format('YYYY')

    const sameMonth = firstMoment.format('MM') === lastMoment.format('MM')
    const sameYear = firstYear === lastYear

    if (sameMonth && sameYear) {
      return `${firstDay} - ${lastDay} ${firstMonth} ${firstYear}`
    } else if (sameYear) {
      return `${firstDay} ${firstMonth} - ${lastDay} ${lastMonth} ${firstYear}`
    } else {
      return `${firstDay} ${firstMonth} ${firstYear} - ${lastDay} ${lastMonth} ${lastYear}`
    }
  }, [daysToRender, skeletonDays, t])

  return (
    <div>
      <WeeklyNavigation
        goToPreviousBlock={goToPreviousBlock}
        goToNextBlock={goToNextBlock}
        isLoading={isLoading}
        wrapperRef={wrapperRef}
        prevButtonRef={prevButtonRef}
        nextButtonRef={nextButtonRef}
        currentDateRange={currentDateRange}
        rangeMode={rangeMode}
        onToggleRangeMode={toggleRangeMode}
      />

      <div
        ref={wrapperRef}
        className="tw-flex tw-flex-col tw-overflow-hidden tw-bg-white"
        style={{
          position: 'relative',
          maxHeight: 'calc(100vh - 235px)',
          minHeight: 'calc(100vh - 235px)',
        }}
      >
        {sortedRange && rangeSummary && (
          <div
            style={{
              position: 'absolute',
              top: 4,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              pointerEvents: 'auto',
            }}
          >
            <button
              onClick={clearRange}
              className="tw-bg-primary"
              style={{
                border: 'none',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              <FaIcon icon={faClose} />
            </button>
            <div
              style={{
                background: '#fff',
                border: '2px solid #fff',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 12,
                color: '#333',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 12,
                  borderRight: '1px solid #ddd',
                  paddingRight: 10,
                  lineHeight: '16px',
                }}
              >
                <div
                  style={{
                    color: 'var(--et-color-semantic-primary-foreground)',
                  }}
                >
                  {rangeSummary.period}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  &Sigma; {rangeSummary.totalSelected}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/driving-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.DRIVING]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/work-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.WORK]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/availability-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.AVAILABILITY]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/rest-black.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.REST]}
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <img
                    src="/assets/icons_nucleo/icons/activity/unknown.svg"
                    width={13}
                    height={13}
                  />
                  {rangeSummary[ActivityType.UNKNOWN]}
                </span>
              </div>
            </div>
          </div>
        )}
        {isLoading && (
          <div className="tw-flex tw-flex-col">
            {skeletonDays.map((date) => (
              <div
                key={`sk-${date}`}
                className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded tw-bg-white tw-shadow-sm"
              >
                <div className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-px-3 tw-py-1.5">
                  <div className="tw-h-3 tw-w-3 tw-animate-pulse tw-rounded tw-bg-gray-200" />
                  <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base">
                    {formatDayHeader(date)}
                  </span>
                </div>
                <TimelineSkeleton
                  width={chartWidth}
                  height={DAY_CHART_HEIGHT}
                  startTime={moment(`${date} 00:00:00`)}
                />
              </div>
            ))}
          </div>
        )}

        {!isLoading && allDays.length === 0 && (
          <div className="tw-flex tw-flex-1 tw-flex-col tw-place-items-center tw-items-center tw-justify-center tw-gap-2">
            <FaIcon icon={faEmptySetSolid} />
            <div className="tw-text-sm tw-text-gray-500">
              {t('common.no-data')}
            </div>
          </div>
        )}

        {!isLoading && visibleDays.length > 0 && (
          <div className="tw-flex tw-flex-col">
            {daysToRender.map((day) => {
              return <DayChart
                companyId={companyId}
                driverId={Number(driverId)}
                showTrips={showTrips}
                showTachographActivities={showTachographActivities}
                driverName={driverName}
                onRefresh={getWeeklyActivity}
                key={day.date}
                day={day}
                chartWidth={chartWidth || 1200}
                data={data}
                t={t}
                onDayClick={handleDayClick}
                vehicleTachographMode={vehicleTachographMode}
                showViolations={showViolations}
                rangeMode={rangeMode}
                rangeStart={sortedRange?.start || null}
                rangeEnd={sortedRange?.end || null}
                pin1Time={pin1Time}
                pin2Time={pin2Time}
                onRangeClick={handleRangeClick}
              />;
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default Weekly
